import theme from 'stories/utils/theme'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import SideBySideInfoCard from 'stories/V1/components/SideBySideInfoCard'
import React, { useEffect, useState } from 'react'
import { useSpring, animated } from 'react-spring'

const Grid = styled.div<{ count: number }>`
    display: grid;
    grid-template-columns: 1fr;
    place-content: center;
    gap: 100px;
    padding: 40px 10%;

    @media (min-width: 1050px) {
        position: relative;
        margin-bottom: calc(-50px * ${({ count }) => count});
    }

    @media (max-width: 750px) {
        padding: 0px;
        gap: 50px;
    }
`

const AnimatedCardWrapper = styled(animated.div)`
    position: relative;
    z-index: 1;
    @media (min-width: 1050px) {
        &.active {
            z-index: 10;
        }
    }
`

export interface FeatureCard {
    assetContent: StaticImageData | React.ReactNode
    onButtonClick?: () => void
    gradient?: string
    customContent?: React.ReactNode
    textSectionColor?: string
}

export const FeatureCardItem = ({
    feature,
    index,
    activeCardIndex,
    isFirstReversed,
    stopRotation,
    textSectionColor,
}: {
    feature: FeatureCard
    index: number
    activeCardIndex: number
    isFirstReversed?: boolean
    stopRotation?: boolean
    textSectionColor?: string
}) => {
    let moveUpAmount = 0

    if (index > 0) {
        for (let i = 0; i < activeCardIndex && i < index; i++) {
            moveUpAmount += 115
        }
    }

    const springProps = useSpring({
        transform: moveUpAmount > 0 && window.innerWidth >= 1050 ? `translateY(-${moveUpAmount}px)` : 'translateY(0px)',
        zIndex: index,
        config: {
            tension: 280,
            friction: 60,
            clamp: false,
            mass: 1.2,
        },
    })

    return (
        <AnimatedCardWrapper style={springProps} className={`feature-card ${activeCardIndex >= index ? 'active' : ''}`}>
            <SideBySideInfoCard
                onButtonClick={feature.onButtonClick}
                assetContent={feature.assetContent}
                gradient={feature.gradient}
                isReversed={isFirstReversed ? index % 2 === 0 : index % 2 !== 0}
                customContent={feature.customContent}
                stopRotation={stopRotation}
                textSectionColor={textSectionColor}
            />
        </AnimatedCardWrapper>
    )
}

interface FeatureEnumerationProps {
    title?: string | JSX.Element
    features: FeatureCard[]
    backgroundColor?: string
    textSectionColor?: string
    borderRadius?: string
    mobileBorderRadius?: string
    isFirstReversed?: boolean
    stopRotation?: boolean
}

const FeatureEnumeration: React.FC<FeatureEnumerationProps> = ({
    title = '',
    features,
    backgroundColor,
    textSectionColor,
    borderRadius,
    mobileBorderRadius,
    isFirstReversed,
    stopRotation,
}) => {
    const [activeCardIndex, setActiveCardIndex] = useState(0)

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerWidth < 1050) return

            const windowHeight = window.innerHeight
            let highestActive = 0

            const cards = document.querySelectorAll('.feature-card')

            cards.forEach((card, idx) => {
                const rect = card.getBoundingClientRect()
                if (rect.top < windowHeight * 0.6) {
                    highestActive = Math.max(highestActive, idx)
                }
            })

            setActiveCardIndex(highestActive)
        }

        window.addEventListener('scroll', handleScroll)

        setTimeout(handleScroll, 100)

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    return (
        <SectionContainer
            backgroundColor={backgroundColor ?? theme.colors.headlineDark}
            title={title}
            borderRadius={borderRadius}
            mobileBorderRadius={mobileBorderRadius}
        >
            <Grid count={features.length}>
                {features.map((feature, index) => (
                    <FeatureCardItem
                        key={index}
                        feature={feature}
                        index={index}
                        activeCardIndex={activeCardIndex}
                        isFirstReversed={isFirstReversed}
                        stopRotation={stopRotation}
                        textSectionColor={textSectionColor}
                    />
                ))}
            </Grid>
        </SectionContainer>
    )
}

export default FeatureEnumeration
